import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, Play, Pause, Volume2, Share2, Check, Music } from 'lucide-react'
import { downloadAudioDirectly } from '../lib/offlineSurveyQueue'

const AudioPlayerPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const rawUrl = searchParams.get('url') || searchParams.get('src') || ''
  const fileName = searchParams.get('name') || searchParams.get('filename') || 'audio_entrevista.mp3'
  const title = searchParams.get('title') || fileName

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const safeFileName = fileName.endsWith('.mp3') ? fileName : `${fileName}.mp3`

  // Gatilho de download direto do arquivo MP3
  const handleDownload = async () => {
    if (!rawUrl) return
    setDownloading(true)
    try {
      await downloadAudioDirectly(rawUrl, safeFileName)
    } finally {
      setDownloading(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch { /* ignore */ }
  }

  const togglePlay = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
  }

  const formatTime = (secs: number) => {
    if (isNaN(secs)) return '00:00'
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  useEffect(() => {
    // Tenta autoplay suave ao carregar a página
    if (audioRef.current && rawUrl) {
      audioRef.current.play().catch(() => {
        // Bloqueio de autoplay do navegador é normal
      })
    }
  }, [rawUrl])

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col justify-between p-4 sm:p-8">
      {/* Top Header */}
      <header className="max-w-3xl w-full mx-auto flex items-center justify-between py-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-600/30">
            <Music size={22} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-white">APP DNA Pesquisas</h1>
            <p className="text-xs text-slate-400 font-medium">pesquisasdna.com.br</p>
          </div>
        </div>

        <button
          onClick={handleCopyLink}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors border border-slate-700"
        >
          {copied ? <Check size={14} className="text-emerald-400" /> : <Share2 size={14} />}
          <span>{copied ? 'Link Copiado' : 'Compartilhar'}</span>
        </button>
      </header>

      {/* Main Card */}
      <main className="max-w-xl w-full mx-auto my-auto py-8">
        <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/80 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-black/50">
          <div className="text-center space-y-2 mb-8">
            <span className="inline-block px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 rounded-full border border-blue-500/20">
              Gravação de Entrevista (.MP3)
            </span>
            <h2 className="text-xl font-bold text-white break-words">
              {title}
            </h2>
            <p className="text-xs text-slate-400">
              Áudio registrado no sistema DNA Pesquisas
            </p>
          </div>

          {/* Player Controls */}
          {rawUrl ? (
            <div className="space-y-6">
              <audio
                ref={audioRef}
                src={rawUrl}
                preload="auto"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={() => {
                  if (audioRef.current) {
                    setCurrentTime(audioRef.current.currentTime)
                  }
                }}
                onLoadedMetadata={() => {
                  if (audioRef.current) {
                    setDuration(audioRef.current.duration)
                  }
                }}
                onEnded={() => setIsPlaying(false)}
              />

              {/* Barra de Progresso */}
              <div className="space-y-2">
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => {
                    const newTime = Number(e.target.value)
                    setCurrentTime(newTime)
                    if (audioRef.current) {
                      audioRef.current.currentTime = newTime
                    }
                  }}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-xs font-mono text-slate-400">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Botões de Controle */}
              <div className="flex items-center justify-center gap-6">
                <button
                  onClick={togglePlay}
                  className="h-16 w-16 rounded-full bg-blue-600 hover:bg-blue-500 active:scale-95 transition-all text-white flex items-center justify-center shadow-lg shadow-blue-600/40"
                  title={isPlaying ? 'Pausar' : 'Reproduzir'}
                >
                  {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
                </button>
              </div>

              {/* Botão de Download */}
              <div className="pt-4 border-t border-slate-700/60 flex flex-col gap-3">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 active:scale-[0.99] text-white font-bold text-sm transition-all shadow-lg shadow-emerald-600/30 disabled:opacity-50"
                >
                  <Download size={18} />
                  <span>{downloading ? 'Baixando...' : 'Baixar Arquivo de Áudio (.mp3)'}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Volume2 size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">Nenhum áudio foi especificado no link.</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-slate-500 py-4">
        &copy; {new Date().getFullYear()} pesquisasdna.com.br — Todos os direitos reservados.
      </footer>
    </div>
  )
}

export default AudioPlayerPage
